import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import EmployeeSelector from '../../events/EmployeeSelector'
import { StatTile } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import DopsList from './DopsList'
import Logbook from './Logbook'
import { apiError } from './dopsShared'

const STATUSES = ['Requested', 'Completed', 'Declined', 'Withdrawn']

// /governance/dops - every DOPS in the organisation (status filter) and any
// clinician's procedure logbook.
const GovernanceDops = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'logbooks' ? 'logbooks' : 'dops'
  const [status, setStatus] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')
  const [clinician, setClinician] = useState(null)

  useEffect(() => {
    if (tab !== 'dops') return
    setLoading(true)
    setFetchError('')
    API.get(`/dops${status ? `?status=${status}` : ''}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) setItems(res.data)
        else if (res.status === 401 || res.status === 403)
          setFetchError('Only the Governance team can view all DOPS.')
        else setFetchError(apiError(res, 'Unable to load DOPS'))
      })
      .catch(() => setFetchError('Unable to load DOPS'))
      .finally(() => setLoading(false))
  }, [tab, status])

  const count = (value) => items.filter((d) => d.status === value).length

  return (
    <Stack gap={1.5} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Box>
        <Typography variant="title2" component="div">
          DOPS &amp; Logbooks
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          Procedure assessments across the organisation and clinician
          procedure logbooks
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(e, value) => setSearchParams({ tab: value }, { replace: true })}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="dops" label="All DOPS" />
          <Tab value="logbooks" label="Clinician logbooks" />
        </Tabs>
      </Box>

      {tab === 'dops' && (
        <Stack gap={1.5}>
          <Box sx={{ overflowX: 'auto' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status}
              onChange={(e, value) => value !== null && setStatus(value)}
            >
              <ToggleButton value="">All</ToggleButton>
              {STATUSES.map((value) => (
                <ToggleButton key={value} value={value}>
                  {value}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          {loading && <LinearProgress />}
          {fetchError && <Alert severity="error">{fetchError}</Alert>}
          {!loading && !fetchError && (
            <>
              {!status && items.length > 0 && (
                <Box
                  display="grid"
                  gap={1}
                  gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
                >
                  <StatTile label="Awaiting assessor" value={String(count('Requested'))} tone="warning" />
                  <StatTile label="Completed" value={String(count('Completed'))} tone="success" />
                  <StatTile label="Declined" value={String(count('Declined'))} tone="error" />
                  <StatTile label="Withdrawn" value={String(count('Withdrawn'))} />
                </Box>
              )}
              <DopsList
                items={items}
                empty="No DOPS match this filter."
              />
            </>
          )}
        </Stack>
      )}

      {tab === 'logbooks' && (
        <Stack gap={2}>
          <Box sx={{ maxWidth: 420 }}>
            <EmployeeSelector
              label="Clinician"
              value={clinician}
              onChange={(e, user) => setClinician(user)}
              fullWidth
              size="small"
            />
          </Box>
          {clinician?._id ? (
            <Logbook endpoint={`/logbook/user/${clinician._id}`} canOpenRecords />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Choose a clinician to view their procedure logbook, skill
              currency and completed DOPS.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default GovernanceDops
